"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { TagVariant } from "@/lib/types";
import Tag from "@/components/ui/tag";
import { Button } from "@/components/ui/button";
import Arrow from "@/components/icons/Arrow";
import { cva } from "class-variance-authority";
import Video from "./Video";
import { cn, strapiMediaAlt, strapiMediaUrl, strapiMediaCaption } from "@/lib/utils";
import { formatDate } from "@/lib/api-helpers";
import ResponsiveImage from "@/components/ui/ResponsiveImage";

interface CardProps {
  variant: TagVariant;
  urlVariant?: string;
  square?: boolean;
  item: any;
  customColor?: string;
}

const websiteId = (process.env.NEXT_PUBLIC_WEBSITE_ID as "nsc" | "jsc") || "nsc";

const imageVariants = cva("relative  w-full overflow-hidden rounded", {
  variants: {
    variant: {
      Speerpunt:
        websiteId === "nsc"
          ? "md:aspect-[1.6] aspect-[1.6] max-h-[250px] md:h-[250px]"
          : "md:aspect-[1.6] aspect-[1.6] max-h-[250px] md:h-[250px] rounded-3xl",
      Nieuws: websiteId === "nsc" ? "aspect-[3/2]" : "aspect-[3/2] rounded-[27px]",
          "Over ons": "aspect-[3/2]",
      Video: "h-[190px]",
      download: "aspect-[0.9] max-h-[300px]",
      square: "aspect-square max-h-[250px]",
      detail: "aspect-[0.9] max-h-[300px]",
    },
  },
  defaultVariants: {
    variant: "Speerpunt",
  },
});

const Card = ({ item, variant, square, urlVariant, customColor }: CardProps) => {
  const pathname = usePathname();
  let { title, thumbnail, tags, slug, role, file, name, type, poster, url, date, publishedAt, summary, province } =
    item.attributes;
  if (tags && Array.isArray(tags)) {
    if (tags[0]?.name) {
      tags = tags.map((tag: any) => tag.name);
    }
  }

  const getLinkText = (variant: TagVariant | "download" | "detail") => {
    switch (variant) {
      case "Speerpunt":
        return "Lees meer";
      case "Nieuws":
        return "Lees meer";
      case "Over ons":
        return "Lees meer";
      case "Europa":
        return "Lees meer over NSC Europa";
      case "download":
        return "Klik open en lees";
      case "Video":
        return "Klik hier om de video te bekijken";
      case "detail":
        return "Klik open en lees";
    }
  };

  const getUrl = (variant: string) => {
    if (variant === "Over ons") variant = type;
    switch (variant) {
      case "Nieuws":
      case "nieuws":
        const baseUrl = `/nieuws/${slug}`;
        // If we're currently on any province page, add 'from' parameter to track source
        if (pathname.startsWith('/provincies/')) {
          return `${baseUrl}?from=${pathname}`;
        }
        // Fallback: If this news item has province data but we're not on a province page,
        // still add the province-specific 'from' parameter for consistency
        if (province?.data?.attributes?.slug) {
          return `${baseUrl}?from=/provincies/${province.data.attributes.slug}/nieuws`;
        }
        return baseUrl;
      case "Speerpunt":
      case "speerpunt":
        return `/standpunten/${slug}`;
      case "download":
        return strapiMediaUrl(file);
      case "tweede-kamerfractie":
        return `/tweede-kamerfractie/${slug}`;
      case "onze-bewindspersonen":
        return `/onze-bewindspersonen/${slug}`;
      case "landelijk-bestuur":
        return `/landelijk-bestuur/${slug}`;
      case "kandidaten":
        return `/kandidaten/${slug}`;
      case "Bestaanszekerheid":
      case "detail":
        return url;
      default:
        return `/${slug}`;
    }
  };

  return (
    <LinkWrapper
      variant={variant}
      external={variant === "download"}
      href={getUrl(urlVariant || variant)}
      className="group h-full"
    >
      <article className="flex h-full flex-col gap-5">
        <figure
          className={imageVariants({
            variant: square ? "square" : (variant as "Speerpunt" | "Nieuws" | "Over ons" | "Video"),
          })}
        >
          {variant === "Video" && file ? (
            <Video src={strapiMediaUrl(file)} poster={strapiMediaUrl(poster)} />
          ) : thumbnail?.data ? (
            <ResponsiveImage
              src={strapiMediaUrl(thumbnail)}
              alt={strapiMediaAlt(thumbnail)}
              caption={strapiMediaCaption(thumbnail)}
              className="rounded object-cover transition-transform duration-500 group-hover:scale-105"
              sizes="(max-width: 600px) 100vw, (max-width: 1200px) 50vw, 33vw"
              fill
            />
          ) : (
            <div className="h-full w-full bg-gray-300" />
          )}
        </figure>

        {tags && tags.length > 0 && variant !== "Over ons" && (
          <div className="flex flex-wrap items-center gap-2">
            {tags.map((tag: any) => (
              <Tag variant={tag} key={tag} />
            ))}
            {variant === "Nieuws" && (date || publishedAt || province?.data?.attributes?.title) && (
              <span className="translate-y-[1px] text-sm font-light">
                {date || publishedAt ? formatDate(date || publishedAt, true) : ""}
                {date && province?.data?.attributes?.title && " - "}
                {province?.data?.attributes?.title || ""}
              </span>
            )}
          </div>
        )}

        <h3
          className={cn(
            variant === "Over ons" ? "!normal-case" : "",
            "max-w-[300px] text-balance font-oswald text-xl font-bold uppercase !leading-normal",
            websiteId === "jsc" && "text-black"
          )}
        >
          {title || name}
        </h3>

        {role && <p className="support -mt-2.5">{role}</p>}

        {summary && <p className="support -mt-2.5 line-clamp-8 text-balance">{summary}</p>}

        {websiteId === "nsc" && (
          <Button variant="link" size="link" className="mt-auto text-wrap text-start">
            {getLinkText(variant)}{" "}
            <Arrow
              color={
                customColor
                  ? customColor
                  : variant === "download" || variant === "detail"
                    ? "var(--color-white)"
                    : "var(--color-nsc-dark-blue)"
              }
              className="ml-3 transition-transform group-hover:translate-x-1"
            />
          </Button>
        )}
      </article>
    </LinkWrapper>
  );
};

const LinkWrapper = ({
  children,
  href,
  external,
  className,
  variant,
}: {
  children: React.ReactNode;
  external?: boolean;
  href: string;
  className?: string;
  variant: TagVariant;
}) => {
  return variant === "Video" ? (
    children
  ) : external ? (
    <a href={href} className={className} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ) : (
    <Link className={className} href={href}>
      {children}
    </Link>
  );
};

export default Card;
